import { useCallback, useEffect } from 'react';
import { useNetPositionQueue } from './useNetPositionQueue';
import { useEnableNetPositionFeature } from './useEnableNetPositionFeature';
import useEventManagerEmitter from '@/event-manager/use-event-manager-emiter';
import { GAME_WINDOW_TO_CLIENT_EVENTS } from '@/event-manager/types';
import { setLockUI } from '@/store/actions';
import { usePopup } from '@/features/Popup/usePopup';
import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import { ERROR_TYPE, POPUP_TYPES } from '@/features/Popup/constants';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const numberFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  signDisplay: 'exceptZero',
});

const NET_POSITION_ERROR_MESSAGES: Record<string, string> = {
  'NP-003':
    'Two outstanding net position calls at start of third spin; third request timed out.',
};

/**
 * Mirrors the queue's lock state into the store (`setLockUI`), pauses/resumes
 * auto-spin via the game client, and routes the user to the lobby (NP-003
 * popup) when the queue indicates repeated failures.
 */
export const useFetchNetPosition = () => {
  const { netPositionFeatureEnabled } = useEnableNetPositionFeature();
  const { netPosition, shouldLock, redirectToLobby, redirectErrorCode } =
    useNetPositionQueue();
  const { emitClientEvent } = useEventManagerEmitter();
  const { showPopup } = usePopup();

  const lockUI = useCallback(() => {
    setLockUI(true);
    emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.AUTO_SPIN_STOP);
  }, [emitClientEvent]);

  const unlockUI = useCallback(() => {
    setLockUI(false);
    emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.AUTO_SPIN_RESUME);
  }, [emitClientEvent]);

  const sendUserToLobby = useCallback(
    (errorCode?: string) => {
      showPopup(
        generatePopupConfig(POPUP_TYPES.PROVIDER, {
          errorConfig: {
            errorType: ERROR_TYPE.NET_POSITION_SIMPLE,
            errorCode,
          },
        }),
        POPUP_TYPES.PROVIDER,
        { errorType: ERROR_TYPE.NET_POSITION_SIMPLE, errorCode },
      );

      Logger.logRumError({
        name: 'net_position',
        description:
          (errorCode && NET_POSITION_ERROR_MESSAGES[errorCode]) ??
          'Net position: unknown error code',
        useCase: DdEventCategory.UserInteraction,
        eventType: DdEventType.Error,
        context: { type: 'NET_POSITION', errorCode },
      });
    },
    [showPopup],
  );

  useEffect(() => {
    if (shouldLock) {
      lockUI();
      if (redirectToLobby) {
        sendUserToLobby(redirectErrorCode);
      }
    } else {
      unlockUI();
    }
  }, [
    lockUI,
    shouldLock,
    unlockUI,
    sendUserToLobby,
    redirectToLobby,
    redirectErrorCode,
  ]);

  return {
    shouldShow: netPositionFeatureEnabled,
    netPosition: numberFormat.format(netPosition),
  };
};
